import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import QrScanner from 'qr-scanner';

interface QRScannerProps {
    open: boolean;
    onScanSuccess: (decodedText: string) => void;
    onScanError?: (error: string) => void;
    onClose: () => void;
    extraContent?: React.ReactNode;
    /**
     * When true, the scanner stays open after a successful scan so multiple
     * codes can be scanned in a row. Repeats of the same UUID are deduped for
     * 5s, and any successful scan triggers a 1.5s cooldown before the next.
     */
    continuous?: boolean;
}

function extractUuid(text: string): string | null {
    const trimmed = text.trim();
    const uuidMatch = trimmed.match(/\/offer\/([a-f0-9-]+)/i);
    if (uuidMatch && uuidMatch[1]) return uuidMatch[1];
    const uuidPattern = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i;
    const match = trimmed.match(uuidPattern);
    if (match) return match[0];
    return null;
}

const QRScanner: React.FC<QRScannerProps> = ({ open, onScanSuccess, onScanError, onClose, extraContent, continuous }) => {
    const [error, setError] = useState<string>('');
    const [debug, setDebug] = useState<string>('Initializing...');
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const scannerRef = useRef<QrScanner | null>(null);
    const doneRef = useRef(false);
    const lastStatusUpdateRef = useRef(0);
    const lastScanUuidRef = useRef<string>('');
    const lastScanTimeRef = useRef<number>(0);
    const continuousRef = useRef(continuous);
    continuousRef.current = continuous;

    const onScanSuccessRef = useRef(onScanSuccess);
    const onScanErrorRef = useRef(onScanError);
    const onCloseRef = useRef(onClose);
    onScanSuccessRef.current = onScanSuccess;
    onScanErrorRef.current = onScanError;
    onCloseRef.current = onClose;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    QrScanner.WORKER_PATH = `${process.env.PUBLIC_URL || ''}/qr-scanner-worker.min.js`;

    const stopScanner = useCallback(() => {
        doneRef.current = true;
        if (scannerRef.current) {
            scannerRef.current.stop();
            scannerRef.current.destroy();
            scannerRef.current = null;
        }
    }, []);

    const startScanner = useCallback(async () => {
        setError('');
        setDebug('Starting scanner...');
        doneRef.current = false;
        lastScanUuidRef.current = '';
        lastScanTimeRef.current = 0;

        const video = videoRef.current;
        if (!video) {
            setDebug('No video element');
            return;
        }

        try {
            const hasCamera = await QrScanner.hasCamera();
            if (!hasCamera) {
                throw new Error('No camera found');
            }

            const scanner = new QrScanner(
                video,
                (result: any) => {
                    if (doneRef.current) return;

                    const decoded = typeof result === 'string' ? result : result?.data || '';
                    const uuid = extractUuid(decoded);

                    if (uuid) {
                        if (continuousRef.current) {
                            const now = Date.now();
                            // Global cooldown after any successful scan
                            if (now - lastScanTimeRef.current < 1500) return;
                            // Dedupe repeated scans of the same code
                            if (uuid === lastScanUuidRef.current && now - lastScanTimeRef.current < 5000) return;
                            lastScanUuidRef.current = uuid;
                            lastScanTimeRef.current = now;
                            onScanSuccessRef.current(uuid);
                            return;
                        }
                        doneRef.current = true;
                        stopScanner();
                        onScanSuccessRef.current(uuid);
                        onCloseRef.current();
                        return;
                    }

                    const message = `QR found but no UUID match: "${decoded}"`;
                    setDebug(message);
                    if (onScanErrorRef.current) onScanErrorRef.current(message);
                },
                {
                    preferredCamera: 'environment',
                    returnDetailedScanResult: true,
                    maxScansPerSecond: 8,
                    highlightScanRegion: true,
                    highlightCodeOutline: true,
                    onDecodeError: (err: any) => {
                        if (doneRef.current) return;
                        const message = String(err?.message || '').toLowerCase();
                        const now = Date.now();

                        // "No QR found" is expected on most frames; keep status neutral.
                        if (!message || message.includes('no qr code found') || message.includes('not found')) {
                            if (now - lastStatusUpdateRef.current > 1200) {
                                setDebug('Scanning...');
                                lastStatusUpdateRef.current = now;
                            }
                            return;
                        }

                        // For unusual decode errors, surface a throttled debug line.
                        if (now - lastStatusUpdateRef.current > 1200) {
                            setDebug(`Scanner status: ${err?.message || 'decode retrying'}`);
                            lastStatusUpdateRef.current = now;
                        }
                    },
                }
            );

            scannerRef.current = scanner;
            scanner.setInversionMode('both');
            await scanner.start();
            setDebug('Camera active');
        } catch (err: any) {
            let msg = 'Failed to start camera. ';
            if (err?.name === 'NotAllowedError') {
                msg += 'Please allow camera access.';
            } else if (err?.name === 'NotFoundError') {
                msg += 'No camera found.';
            } else {
                msg += err?.message || 'Check permissions.';
            }
            setError(msg);
            setDebug(`Error: ${err?.name || 'unknown'} - ${err?.message || 'unknown error'}`);
            if (onScanErrorRef.current) onScanErrorRef.current(msg);
        }
    }, [stopScanner]);

    useEffect(() => {
        if (!open) {
            stopScanner();
        }
    }, [open, stopScanner]);

    useEffect(() => {
        return () => {
            stopScanner();
        };
    }, [stopScanner]);

    const handleClose = () => {
        stopScanner();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            TransitionProps={{
                onEntered: () => {
                    if (open) startScanner();
                },
            }}
            PaperProps={{
                sx: { m: { xs: 0, sm: 2 }, maxHeight: { xs: '100%', sm: '90vh' } }
            }}
        >
            <DialogTitle>Scan QR Code</DialogTitle>
            <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
                {extraContent && <Box sx={{ px: 1, pt: 1, pb: 0 }}>{extraContent}</Box>}
                <Box sx={{ py: { xs: 1, sm: 2 } }}>
                    <Box sx={{
                        width: '100%',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: '#000',
                        '& video': { width: '100%', display: 'block' },
                    }}>
                        <video ref={videoRef} playsInline muted />
                    </Box>

                    <Typography
                        variant="caption"
                        sx={{
                            mt: 1,
                            display: 'block',
                            textAlign: 'center',
                            color: 'text.secondary',
                            wordBreak: 'break-all',
                            fontSize: '11px',
                        }}
                    >
                        {debug}
                    </Typography>
                    {error && (
                        <Typography variant="body2" color="error" sx={{ mt: 1, textAlign: 'center' }}>
                            {error}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, justifyContent: 'flex-end' }}>
                <Button onClick={handleClose} color="primary">
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default QRScanner;
